import { ApplicationConfig, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';
import { routes } from './app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { NotificationPushService } from './services/notificationPush.service';

const customPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '#f0f0ff',
      100: '#e6e5ff',
      200: '#d1cfff',
      300: '#b3b0ff',
      400: '#8b87ff',
      500: '#5954ff',
      600: '#2A28C3',
      700: '#1f1e9e',
      800: '#18177a',
      900: '#131265',
      950: '#0a0940',
    },
    // Lara usa primary.500 como color base; lo alineamos con --primary-color (600)
    // y --primary-color-hover (700) de styles.scss para que todos los componentes
    // tomen el color de marca sin necesidad de styleClass manual.
    colorScheme: {
      light: {
        primary: {
          color: '{primary.600}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.700}',
          activeColor: '{primary.800}',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    providePrimeNG({
      theme: {
        preset: customPreset,
        options: {
          animation: 'smooth',
          darkModeSelector: false,
        },
      },
      // Textos de PrimeNG en español para toda la app (calendarios, filtros de
      // tablas, carga de archivos, contraseña, etc.). Un componente puede seguir
      // sobrescribiendo lo suyo (p.ej. dateFormat en cada p-datepicker).
      translation: {
        // Calendario (p-datepicker)
        dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
        monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'],
        monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Set', 'Oct', 'Nov', 'Dic'],
        dateFormat: 'dd/mm/yy',
        firstDayOfWeek: 0, // domingo, como el calendario de Perú
        today: 'Hoy',
        clear: 'Limpiar',
        weekHeader: 'Sem',
        chooseYear: 'Elegir año',
        chooseMonth: 'Elegir mes',
        chooseDate: 'Elegir fecha',
        prevDecade: 'Década anterior',
        nextDecade: 'Década siguiente',
        prevYear: 'Año anterior',
        nextYear: 'Año siguiente',
        prevMonth: 'Mes anterior',
        nextMonth: 'Mes siguiente',
        prevHour: 'Hora anterior',
        nextHour: 'Hora siguiente',
        prevMinute: 'Minuto anterior',
        nextMinute: 'Minuto siguiente',
        prevSecond: 'Segundo anterior',
        nextSecond: 'Segundo siguiente',
        am: 'a. m.',
        pm: 'p. m.',

        // Filtros de columnas (p-table / p-columnFilter)
        startsWith: 'Empieza con',
        contains: 'Contiene',
        notContains: 'No contiene',
        endsWith: 'Termina con',
        equals: 'Igual a',
        notEquals: 'Distinto de',
        noFilter: 'Sin filtro',
        lt: 'Menor que',
        lte: 'Menor o igual que',
        gt: 'Mayor que',
        gte: 'Mayor o igual que',
        is: 'Es',
        isNot: 'No es',
        before: 'Antes',
        after: 'Después',
        dateIs: 'Fecha igual a',
        dateIsNot: 'Fecha distinta de',
        dateBefore: 'Fecha anterior a',
        dateAfter: 'Fecha posterior a',
        apply: 'Aplicar',
        matchAll: 'Coincidir todos',
        matchAny: 'Coincidir cualquiera',
        addRule: 'Agregar regla',
        removeRule: 'Quitar regla',

        // Confirmaciones y carga de archivos
        accept: 'Sí',
        reject: 'No',
        choose: 'Elegir',
        upload: 'Subir',
        cancel: 'Cancelar',
        pending: 'Pendiente',
        fileChosenMessage: '{0} archivo(s)',
        noFileChosenMessage: 'Ningún archivo seleccionado',
        fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],

        // Contraseña (p-password)
        weak: 'Débil',
        medium: 'Media',
        strong: 'Fuerte',
        passwordPrompt: 'Ingresa una contraseña',

        // Mensajes vacíos / accesibilidad de listas
        emptyMessage: 'Sin resultados.',
        emptySearchMessage: 'Sin resultados.',
        emptyFilterMessage: 'Sin resultados.',
        emptySelectionMessage: 'Ningún elemento seleccionado',
        searchMessage: '{0} resultados disponibles',
        selectionMessage: '{0} elementos seleccionados',
      }
    }),

    MessageService,
    DialogService,
  ],
};